// "Grant XP" panel shown on the detail page of a manual ingestor.
// Posts a single event to /api/ingest/:instance_id. The api's manual
// plugin requires admin role, which the caller's session already has.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import type { ScoringRule } from './types';

interface User {
  id: string;
  handle: string;
  display_name: string;
  email: string;
}

interface Competency {
  id: string;
  name: string;
  slug: string;
  dimension: 'technical' | 'behavioral';
}

export function GrantXpPanel({ instanceId, teamId }: { instanceId: string; teamId: string }) {
  const qc = useQueryClient();
  const users = useQuery<{ users: User[] }>({
    queryKey: ['users'],
    queryFn: () => api<{ users: User[] }>('/api/users'),
  });
  const rules = useQuery<{ rules: ScoringRule[] }>({
    queryKey: ['scoring-rules', teamId],
    queryFn: () => api<{ rules: ScoringRule[] }>(`/api/admin/scoring-rules?team_id=${encodeURIComponent(teamId)}`),
  });
  const competencies = useQuery<{ competencies: Competency[] }>({
    queryKey: ['competencies'],
    queryFn: () => api<{ competencies: Competency[] }>('/api/competencies'),
  });

  const [userId, setUserId] = useState('');
  const [eventType, setEventType] = useState('');
  const [note, setNote] = useState('');
  const [explicitXp, setExplicitXp] = useState<number | ''>('');
  const [competencyOverride, setCompetencyOverride] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const grant = useMutation({
    mutationFn: async () => {
      const selectedUser = (users.data?.users ?? []).find((u) => u.id === userId);
      if (!selectedUser) throw new Error('Pick a user.');
      if (!eventType.trim()) throw new Error('Event type is required.');

      // Look up the matching rule (first match by event_type, lowest priority
      // wins) to preview the XP + competency. We don't *enforce* this — the
      // admin can override below.
      const matchingRules = (rules.data?.rules ?? [])
        .filter((r) => r.event_type === eventType.trim() && r.enabled)
        .sort((a, b) => a.priority - b.priority);
      const matched = matchingRules[0];

      const payload: Record<string, unknown> = {
        type: eventType.trim(),
        userHandle: selectedUser.handle,
      };
      if (note.trim()) payload.note = note.trim();
      if (explicitXp !== '') {
        // We don't have an "override XP" field in the api's manual plugin
        // today; the XP comes from the matching scoring rule. So we surface
        // the override as a payload field for the admin's records. A
        // future change can let admins override XP directly. For now we
        // just include it in the event so the audit log shows intent.
        payload.xpOverride = Number(explicitXp);
      }
      if (competencyOverride) {
        payload.competencyOverride = competencyOverride;
      }
      if (matched) {
        payload.matchedRule = { id: matched.id, splits: matched.splits };
      }

      const result = await api<{ ok: true; accepted: number; ignored: number }>(
        `/api/ingest/${instanceId}`,
        { method: 'POST', body: payload },
      );
      return { result, matched };
    },
    onSuccess: (data) => {
      const xpMsg = data.matched
        ? data.matched.splits.length > 0
          ? 'Awarded ' + data.matched.splits.map((s) => s.xp + ' XP to ' + s.competency_id).join(', ') + '.'
          : 'Matching rule had no awards — event recorded but no XP awarded.'
        : 'No matching scoring rule — event recorded but no XP awarded.';
      setOk(`Grant accepted. ${xpMsg}`);
      setErr(null);
      setNote('');
      // The events table for this instance should refresh; the player
      // dashboard (cross-instance) won't auto-refresh but a manual
      // navigate back to /player will pick it up.
      qc.invalidateQueries({ queryKey: ['ingestor-instance-events', instanceId] });
    },
    onError: (e) => {
      setErr(e instanceof ApiError ? e.message : (e as Error).message ?? 'Grant failed.');
      setOk(null);
    },
  });

  // Event-type typeahead: existing enabled rules for this team, deduped.
  const eventTypeSuggestions: string[] = Array.from(
    new Set((rules.data?.rules ?? []).filter((r) => r.enabled).map((r) => r.event_type)),
  ).sort();

  return (
    <div className="card space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Grant XP</h2>
        <p className="text-xs text-ink-dim">
          Record a single event attributed to one user. Scoring rules pick the XP + competency;
          leave the overrides blank to use the rule defaults.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          setOk(null);
          grant.mutate();
        }}
        className="grid gap-3 sm:grid-cols-2"
      >
        <div className="space-y-1">
          <label className="label" htmlFor="grant-user">User</label>
          <select
            id="grant-user"
            className="input"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            required
          >
            <option value="">Select…</option>
            {(users.data?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name} (@{u.handle})
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="grant-type">Event type</label>
          <input
            id="grant-type"
            className="input font-mono"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            list="grant-type-list"
            placeholder="survey.completed"
            required
          />
          <datalist id="grant-type-list">
            {eventTypeSuggestions.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="grant-xp">XP override (optional)</label>
          <input
            id="grant-xp"
            className="input"
            type="number"
            min="0"
            value={explicitXp}
            onChange={(e) => setExplicitXp(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder="Use rule default"
          />
          <p className="text-xs text-ink-dim">
            Recorded with the event for audit. Scoring still uses the rule's XP today.
          </p>
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="grant-comp">Competency override (optional)</label>
          <select
            id="grant-comp"
            className="input"
            value={competencyOverride}
            onChange={(e) => setCompetencyOverride(e.target.value)}
          >
            <option value="">Use rule default</option>
            {(competencies.data?.competencies ?? []).map((c: Competency) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.dimension})
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1 sm:col-span-2">
          <label className="label" htmlFor="grant-note">Note (optional)</label>
          <input
            id="grant-note"
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Q3 training survey completed"
            maxLength={500}
          />
        </div>
        {err ? <p className="text-sm text-accent-danger sm:col-span-2" role="alert">{err}</p> : null}
        {ok ? <p className="text-sm text-accent-success sm:col-span-2" role="status">{ok}</p> : null}
        <div className="sm:col-span-2 flex justify-end">
          <button type="submit" className="btn-primary" disabled={grant.isPending}>
            {grant.isPending ? 'Granting…' : 'Grant'}
          </button>
        </div>
      </form>
    </div>
  );
}
